import { ReactElement } from 'react';
import { Paper } from '@mui/material';
import { Address } from 'viem';

import { mapDataToUndefinedIfSkip } from 'modules/api/utils';
import { useConnection } from 'modules/auth/hooks/useConnection';
import { ClaimTable } from 'modules/delegateStaking/components/ClaimTable';
import { useGetAccountPoolQuery } from 'modules/pool/actions/getAccountPool';
import { useGetPoolPendingUnstakesQuery } from 'modules/pool/actions/getPoolPendingUnstakes';
import { FirstDelegate } from 'modules/pool/components/FirstDelegate/FirstDelegate';
import { MyPosition } from 'modules/pool/components/MyPosition/MyPosition';

import { useStyles } from './useStyles';

interface IAccountDetailsProps {
  poolAddress: Address;
}

export function AccountDetails({
  poolAddress,
}: IAccountDetailsProps): ReactElement {
  const { classes } = useStyles();

  const { isConnected, connectionKey, cacheTime, isSafeAccount } =
    useConnection();

  const { data: accountPool } = useGetAccountPoolQuery(
    { connectionKey, address: poolAddress },
    {
      skip: !isConnected,
      selectFromResult: mapDataToUndefinedIfSkip,
      refetchOnMountOrArgChange: cacheTime,
    },
  );

  const { data: poolUnstakes } = useGetPoolPendingUnstakesQuery(
    {
      poolAddress,
    },
    {
      skip: !isConnected || !poolAddress,
      selectFromResult: mapDataToUndefinedIfSkip,
      refetchOnMountOrArgChange: cacheTime,
      pollingInterval: isSafeAccount ? cacheTime : undefined,
    },
  );

  const isStakedAmount =
    !!accountPool?.stakedAmount && !accountPool.stakedAmount.isZero();
  const isFistTime = !isConnected || (!isStakedAmount && !poolUnstakes?.length);

  if (isFistTime) {
    return <FirstDelegate poolAddress={poolAddress} />;
  }

  return (
    <Paper className={classes.root}>
      {isStakedAmount && <MyPosition poolAddress={poolAddress} />}

      {!!poolUnstakes?.length && (
        <ClaimTable
          isHidePoolName
          className={classes.table}
          pendingUnstakes={poolUnstakes}
        />
      )}
    </Paper>
  );
}
